import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useOutletContext } from "react-router";

import { Button } from "primereact/button";
import { Checkbox } from "primereact/checkbox";
import { Dropdown } from "primereact/dropdown";
import { classNames } from "primereact/utils";
import { FormInput } from "../../../components/FormInput";
import FormNumberInput from "../../../components/FormNumberInput";
import { FormTextarea } from "../../../components/FormTextarea";
import { useEditableFlag } from "../../../hooks/useEditableFlag";
import { SystemLoading, checkLoading } from "../../../store/loadingStore";
import { MessageContext, handleSuccessMessage } from "../../../store/messageStore";
import { getSubItemsByItemNo } from "../../../utility/service/api-service";
import { F1010302Before, createCarbonPlan, updateCarbonPlan } from "../../../utility/service/carbon-service";
import {
  Base64Encoder,
  BcryptEncoder,
  checkDpSuccess,
  getFormErrorMessage,
} from "../../../utility/service/tool-service";
import { addFormValidator } from "../../../utility/validate/formValidator";
import { isGeneral, isManufacturing } from "../constants/inventoryType";

const Information = ({ setDisabledTab, setActiveIndex }) => {
  const { t } = useTranslation();
  const isEditable = useEditableFlag("F10103"); // control edit by F10103
  const [, loading] = useContext(SystemLoading);
  const [gwpBasis, setGwpBasis] = useState("Y");
  const [planYearList, setPlanYearList] = useState([]); //盤查年度
  const [planCauseList, setPlanCauseList] = useState([]); //盤查原因
  const [gwpVersionList, setGwpVersionList] = useState([]); //gwpVersion
  const [criterionList, setCriterion] = useState([]); //規範依據
  const [verAgencyList, setVerAgencyList] = useState([]); //查驗證機構
  const [planTypeList, setPlanTypeList] = useState([]); //清冊類型
  const [regulatedIndustryList, setRegulatedIndustryList] = useState([]); //納管行業別
  const [regulatedConditionList, setRegulatedConditionList] = useState([]); //納管條件
  const [, dispatch] = useContext(MessageContext); //訊息視窗
  const { itemObj, setAction, action, setItemObj } = useOutletContext();
  //表單驗證
  const [formRule, setFormRule] = useState([]);
  const [tab1Action, setTab1Action] = useState(action === "create" ? "create" : "detail");

  const {
    getValues,
    setValue,
    watch,
    control,
    register,
    handleSubmit,
    clearErrors,
    trigger,
    formState: { errors, isValid, isDirty },
  } = useForm({
    defaultValues: {},
    //錯誤驗證時機
    mode: "all",
  });

  // 編碼
  const encoder = (dataList, code) => {
    const findIndex = (value, list) => {
      let result = list?.filter((item) => item.subitemNo === value);
      return result.length > 0 ? result[0].index : false;
    };
    if (!isNotBlank(code) || !isNotBlank(dataList) || dataList.length <= 0) return null;
    return Base64Encoder(BcryptEncoder(code)) + "," + findIndex(code, dataList);
  };

  const isNotBlank = (value) => {
    return value !== undefined && value != null && value !== "";
  };

  const onSubmit = (data) => {
    checkLoading(loading);
    let reqBody = {
      year: encoder(planYearList, data.year),
      companySeq: itemObj?.companySeq,
      cause: encoder(planCauseList, data.cause),
      criterion: encoder(criterionList, data.criterion),
      baseYear: data.baseYear === true ? "Y" : "N",
      status: "0",
      gwpType: gwpBasis,
      memo: data.memo,
      isSuspend: "N",
      //v0.75增加
      verified: data.verified ? "Y" : "N",
      significance: data.significance?.toString(),
      materiality: data.materiality?.toString(),
      exclude: data.exclude?.toString(),
      //是否經重大性鑑別
      importanceIdentification: data.importanceIdentification ? "Y" : "N",
      planType: encoder(planTypeList, data.planType),
      regulatedIndustry: encoder(regulatedIndustryList, data.regulatedIndustry),
      regulatedCondition: encoder(regulatedConditionList, data.regulatedCondition),
      verificationAgency: data.verificationAgency,
      inspectionAgency: encoder(verAgencyList, data.inspectionAgency),
    };

    let gwpVersion = "";
    if (reqBody.gwpType === "Y") {
      gwpVersion = data.gwpVersion;
    } else if (reqBody.gwpType === "M") {
      for (let i = 1; i < 13; i++) {
        gwpVersion += data[`gwp_${i}`];
      }
    }
    reqBody.gwpVersion = gwpVersion;


    let displayCause = planCauseList?.filter((item) => item.subitemNo == data.cause)?.[0]?.subitemName;
    let displayCriterion = criterionList?.filter((item) => item.subitemNo == data.criterion)?.[0]?.subitemName;
    let displayGwp = "";
    Array.from(new Set(gwpVersion.split("")))?.filter((val) =>
      gwpVersionList.find((arrayObj) => {
        if (arrayObj?.subitemNo === val) {
          displayGwp += arrayObj?.subitemName + "/";
        }
      }),
    );

    if (tab1Action === "create") {
      //建立
      createCarbonPlan(reqBody)
        .then((res) => {
          if (checkDpSuccess(res)) {
            setItemObj((pre) => {
              return {
                ...pre,
                planSn: res.data.RespBody?.planSn,
                criterion: data.criterion,
                cause: data.cause,
                displayGwp: displayGwp?.slice(0, -1), //將最後一個/刪除
                year: data.year,
                displayCause: displayCause,
                displayCriterion: displayCriterion,
                gwpType: gwpBasis,
                gwpVersion: reqBody.gwpVersion,
                baseYear: data.baseYear === true ? "Y" : "N",
                memo: data.memo,
                //v0.75增加
                verified: data.verified ? "Y" : "N",
                // 是否經重大性鑑別
                importanceIdentification: data.importanceIdentification ? "Y" : "N",
                verificationAgency: data.verified ? data.verificationAgency : null,
                significance: data.significance,
                materiality: data.materiality,
                exclude: data.exclude,
                planType: data.planType,
                planTypeName: planTypeList?.find((item) => item.subitemNo === data.planType)?.subitemName,
                inspectionAgency: data.inspectionAgency,
                regulatedIndustry: data.regulatedIndustry,
                regulatedCondition: data.regulatedCondition,
              };
            });
            setDisabledTab(false); //點確認後可前往下一個頁籤
            setActiveIndex(1); //Y 切換到組織邊界
            handleSuccessMessage(dispatch, t("button.create") + " " + t("message.success"));
            setTab1Action("detail"); //新增完成後變成可編輯狀態
            setAction("edit");
          }
        })
        .finally(() => {
          checkLoading(loading, false);
        });
    } else {
      reqBody.planSn = itemObj?.planSn;
      updateCarbonPlan(reqBody)
        .then((res) => {
          if (checkDpSuccess(res)) {
            setItemObj((pre) => {
              //更新時也要一併將目前的盤查原因 規範依據資料同步更新 2024/03/23
              return {
                ...pre,
                criterion: data.criterion,
                cause: data.cause,
                displayGwp: displayGwp?.slice(0, -1), //將最後一個/刪除
                year: data.year,
                displayCause: displayCause,
                displayCriterion: displayCriterion,
                gwpType: gwpBasis,
                gwpVersion: reqBody.gwpVersion,
                baseYear: data.baseYear === true ? "Y" : "N",
                memo: data.memo,
                verified: data.verified ? "Y" : "N",
                verificationAgency: data.verificationAgency,
                significance: data.significance,
                materiality: data.materiality,
                exclude: data.exclude,
                importanceIdentification: data.importanceIdentification ? "Y" : "N",
                inspectionAgency: data.inspectionAgency,
                regulatedIndustry: data.regulatedIndustry,
                regulatedCondition: data.regulatedCondition,
              };
            });
            setActiveIndex(1); //Y 切換到組織邊界
            handleSuccessMessage(dispatch, t("button.update") + " " + t("message.success"));
            setTab1Action("detail"); //更新完成後變成可編輯狀態
          }
        })
        .finally(() => {
          checkLoading(loading, false);
        });
    }
  };

  const monthGwpVersion = [
    {
      month: "Jan",
      value: "",
    },
    {
      month: "Feb",
      value: "",
    },
    {
      month: "Mar",
      value: "",
    },
    {
      month: "Apr",
      value: "",
    },
    {
      month: "May",
      value: "",
    },
    {
      month: "Jun",
      value: "",
    },
    {
      month: "Jul",
      value: "",
    },
    {
      month: "Aug",
      value: "",
    },
    {
      month: "Sep",
      value: "",
    },
    {
      month: "Oct",
      value: "",
    },
    {
      month: "Nov",
      value: "",
    },
    {
      month: "Dec",
      value: "",
    },
  ];

  useEffect(() => {
    if (tab1Action === "update" || tab1Action === "detail") {
      setValue("year", itemObj?.year, { shouldValidate: true });
      setValue("cause", itemObj?.cause, { shouldValidate: true });
      setValue("criterion", itemObj?.criterion, { shouldValidate: true });
      setValue("baseYear", itemObj?.baseYear === "Y" ? true : false, { shouldValidate: true });
      setGwpBasis(itemObj?.gwpType);
      // 是否已查證
      setValue("verified", itemObj?.verified === "Y" ? true : false);
      setValue("verificationAgency", itemObj?.verificationAgency, { shouldValidate: true });
      // 是否經重大性鑑別
      setValue("importanceIdentification", itemObj?.importanceIdentification === "Y" ? true : false);
      //門檻
      setValue("significance", itemObj?.significance, { shouldValidate: true });
      setValue("materiality", itemObj?.materiality, { shouldValidate: true });
      setValue("exclude", itemObj?.exclude, { shouldValidate: true });
      setValue("planType", itemObj?.planType, { shouldValidate: true });
      setValue("inspectionAgency", itemObj?.inspectionAgency, { shouldValidate: true });
      setValue("regulatedIndustry", itemObj?.regulatedIndustry, { shouldValidate: true });
      setValue("regulatedIndustry", itemObj?.regulatedIndustry, { shouldValidate: true });
      setValue("regulatedCondition", itemObj?.regulatedCondition, { shouldValidate: true });

      if (itemObj?.gwpType === "Y") {
        setValue("gwpVersion", itemObj?.gwpVersion, { shouldValidate: true });
        trigger("gwpVersion");
      } else {
        let gwpVersion = itemObj?.gwpVersion;
        gwpVersion = gwpVersion?.split("");
        gwpVersion?.map((item, index) => {
          setValue(`gwp_${index + 1}`, item, { shouldValidate: true });
          trigger(`gwp_${index + 1}`);
        });
      }
      setValue("memo", itemObj?.memo);
      trigger("year");
      trigger("cause");
      trigger("criterion");
      trigger("verificationAgency");
      trigger("significance");
      trigger("materiality");
      trigger("exclude");
      clearErrors(["significance", "materiality", "exclude", "verificationAgency"]);
    }
  }, [tab1Action]);

  //避免觸發 按enter 後 就觸發 onSubmit 事件冒泡 給input 使用 <FormInput enterClick事件 ; 一般input onKeyDown事件
  const inputCheckKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
    }
  };

  useEffect(() => {
    axios_getSubItemsByItemNo("CFV_PLAN_YEAR", 0).then(setPlanYearList);
    axios_getSubItemsByItemNo("CFV_PLAN_CAUSE", 2).then(setPlanCauseList);
    axios_getSubItemsByItemNo("GWP_VERSION", 3).then(setGwpVersionList);
    axios_getSubItemsByItemNo("CFV_PLAN_CRITERION", 4).then(setCriterion);
    axios_getSubItemsByItemNo("INVENTORY_TYPE", 42).then(setPlanTypeList);
    axios_getSubItemsByItemNo("INSPECTION_AGENCY", 45).then(setVerAgencyList);
    axios_getSubItemsByItemNo("REGULATED_INDUSTRY", 43).then(setRegulatedIndustryList);
    axios_getSubItemsByItemNo("REGULATED_CONDITION", 44).then(setRegulatedConditionList);
    //    axios_getSubItemsByItemNo("BUSINESS_AUTHORITIES", 47).then(setBusinessAuthoritiesList);
    axios_getCreateBefore(); //before統一檢核表單驗證
  }, []);

  const axios_getSubItemsByItemNo = (itemNo, orderNo) => {
    return getSubItemsByItemNo(itemNo, orderNo).then((res) => {
      if (checkDpSuccess(res)) {
        let data = res.data.RespBody.subItems;
        return data.map((item, index) => ({
          ...item,
          index,
        }));
      }
      return [];
    });
  };

  //建立前查詢form 規則
  const axios_getCreateBefore = () => {
    F1010302Before().then((res) => {
      if (checkDpSuccess(res)) {
        let array = [];
        res?.data?.RespBody?.constraints?.forEach((item) => {
          const result = addFormValidator(item?.field, res.data.RespBody.constraints);
          array.push(result);
        });
        setFormRule(array);
        if (tab1Action === "create") {
          setValue("significance", 3.0);
          setValue("materiality", 5.0);
          setValue("exclude", 0.5);
          setValue("importanceIdentification", true); //是否經重大性鑑別 default Y
        }
      }
    });
  };

  const setValidForm = useCallback(
    (formId) => {
      if (formRule?.length > 0) {
        const tmp = formRule?.find((item) => {
          return Object.keys(item)[0] === formId;
        });
        return tmp ? tmp[formId] : "";
      }
    },
    [formRule],
  );

  // 納管行業別依清冊類型篩選：REGULATED_INDUSTRY.param1 以 | 合併清冊類型代碼（0~6），只顯示符合當前 planType 的選項
  const planTypeForFilter = watch("planType");
  const filteredRegulatedIndustryList = useMemo(() => {
    if (planTypeForFilter == null || planTypeForFilter === "" || !regulatedIndustryList?.length) return [];
    const planTypeStr = String(planTypeForFilter).trim();
    return regulatedIndustryList.filter((i) => {
      const param1Str = String(i.param1 ?? "").trim();
      if (!param1Str) return false;
      const allowedTypes = param1Str
        .split("|")
        .map((s) => s.trim())
        .filter(Boolean);
      return allowedTypes.includes(planTypeStr);
    });
  }, [planTypeForFilter, regulatedIndustryList]);

  // 納管條件依納管行業別篩選：REGULATED_CONDITION.param1 以 | 合併多個 REGULATED_INDUSTRY.itemId，需包含所選行業的 itemId
  const selectedRegulatedIndustry = watch("regulatedIndustry");
  const filteredRegulatedConditionList = useMemo(() => {
    if (!selectedRegulatedIndustry || !regulatedIndustryList?.length || !regulatedConditionList?.length) {
      return [];
    }
    const industryItem = regulatedIndustryList.find((i) => i.subitemNo === selectedRegulatedIndustry);
    const industryItemIdStr = industryItem?.itemId != null ? String(industryItem.itemId) : null;
    if (!industryItemIdStr) return [];
    return regulatedConditionList.filter((c) => {
      const param1Str = String(c.param1 ?? "").trim();
      if (!param1Str) return false;
      const ids = param1Str
        .split("|")
        .map((s) => s.trim())
        .filter(Boolean);
      return ids.includes(industryItemIdStr);
    });
  }, [selectedRegulatedIndustry, regulatedIndustryList, regulatedConditionList]);

  // 當納管行業別變更時，若目前納管條件不在新篩選結果內則清空
  useEffect(() => {
    const currentCondition = watch("regulatedCondition");
    if (!currentCondition || !filteredRegulatedConditionList.length) return;
    const stillValid = filteredRegulatedConditionList.some((c) => c.subitemNo === currentCondition);
    if (!stillValid) {
      setValue("regulatedCondition", null);
    }
  }, [selectedRegulatedIndustry, filteredRegulatedConditionList]);

  // 當清冊類型變更時，若目前納管行業別/納管條件不在新篩選結果內則清空
  useEffect(() => {
    const currentIndustry = watch("regulatedIndustry");
    if (!currentIndustry || !filteredRegulatedIndustryList.length) return;
    const stillValid = filteredRegulatedIndustryList.some((i) => i.subitemNo === currentIndustry);
    if (!stillValid) {
      setValue("regulatedIndustry", null);
      setValue("regulatedCondition", null);
    }
  }, [planTypeForFilter, filteredRegulatedIndustryList]);

  // 事業主管機關、證書字號、納管行業別、納管條件：僅在「非通用行業」且「非製造業」時顯示
  const planType = watch("planType");
  const showRegulatedFields = planType != null && !isGeneral(planType) && !isManufacturing(planType);

  return (
    <form className="grid" onSubmit={handleSubmit(onSubmit)}>
      {/* 盤查年度 */}
      <div className="col-12 lg:col-4">
        <label htmlFor="year" className={`form-label ${tab1Action === "detail" ? "" : "required"}`}>
          {t("inventory_year")}
        </label>
        <Controller
          name="year"
          control={control}
          rules={setValidForm("year")}
          render={({ field, fieldState }) => (
            <Dropdown
              disabled={tab1Action === "detail"}
              appendTo={"self"}
              optionLabel="subitemName"
              optionValue="subitemNo"
              value={field.value}
              virtualScrollerOptions={{ itemSize: 40 }}
              defaultValue={""}
              placeholder={t("plz_chs")}
              name="year"
              control={control}
              options={planYearList}
              className={`w-full ` + classNames({ "p-invalid": fieldState.error })}
              onChange={(event) => {
                field.onChange(event.value);
              }}
            />
          )}
        />
      </div>
      {/* 盤查原因 */}
      <div className="col-12 lg:col-4">
        <label htmlFor="cause" className={`form-label ${tab1Action === "detail" ? "" : "required"}`}>
          {t("inventory_reason")}
        </label>
        <Controller
          name="cause"
          control={control}
          rules={setValidForm("cause")}
          render={({ field, fieldState }) => (
            <Dropdown
              disabled={tab1Action === "detail"}
              appendTo={"self"}
              optionLabel="subitemName"
              optionValue="subitemNo"
              value={field.value}
              defaultValue={""}
              placeholder={t("plz_chs")}
              name="cause"
              control={control}
              options={planCauseList}
              className={`w-full ` + classNames({ "p-invalid": fieldState.error })}
              onChange={(event) => {
                field.onChange(event.value);
              }}
            />
          )}
        />
      </div>
      {/* 規範依據 */}
      <div className="col-12 lg:col-4">
        <label htmlFor="criterion" className={`form-label ${tab1Action === "detail" ? "" : "required"}`}>
          {t("standard_basis")}
        </label>
        <Controller
          name="criterion"
          control={control}
          rules={setValidForm("criterion")}
          render={({ field, fieldState }) => (
            <Dropdown
              disabled={tab1Action === "detail"}
              appendTo={"self"}
              optionLabel="subitemName"
              optionValue="subitemNo"
              value={field.value}
              defaultValue={""}
              placeholder={t("plz_chs")}
              name="criterion"
              control={control}
              options={criterionList}
              className={`w-full ` + classNames({ "p-invalid": fieldState.error })}
              onChange={(event) => {
                field.onChange(event.value);
                if (event.value === "0") {
                  //環境部預設AR5 ISO預設AR6
                  setValue("gwpVersion", "5");
                } else if (event.value === "1") {
                  setValue("gwpVersion", "6");
                }
              }}
            />
          )}
        />
      </div>
      {gwpBasis === "Y" ? (
        <div className="col-12 lg:col-4">
          <label htmlFor="gwp" className={`form-label ${tab1Action === "detail" ? "" : "required"}`}>
            GWP Version
          </label>
          <Controller
            name="gwpVersion"
            control={control}
            rules={gwpBasis === "Y" ? setValidForm("criterion") : ""}
            render={({ field, fieldState }) => (
              <Dropdown
                disabled={tab1Action === "detail"}
                appendTo={"self"}
                optionLabel="subitemName"
                optionValue="subitemNo"
                value={field.value}
                virtualScrollerOptions={{ itemSize: 40 }}
                defaultValue={""}
                placeholder={t("plz_chs")}
                name="gwpVersion"
                control={control}
                options={gwpVersionList}
                className={`w-full ` + classNames({ "p-invalid": fieldState.error })}
                onChange={(event) => {
                  field.onChange(event.value);
                }}
              />
            )}
          />
        </div>
      ) : (
        monthGwpVersion.map((m, key) => {
          return (
            <div className="col-6 lg:col-4" key={key}>
              <label
                // htmlFor="gwp"
                className="form-label"
              >
                {t(m.month)}
              </label>
              <Controller
                name={`gwp_${key + 1}`}
                control={control}
                rules={gwpBasis === "M" ? setValidForm("criterion") : ""}
                render={({ field, fieldState }) => (
                  <Dropdown
                    disabled={tab1Action === "detail"}
                    appendTo={"self"}
                    optionLabel="subitemName"
                    optionValue="subitemNo"
                    value={field.value}
                    virtualScrollerOptions={{ itemSize: 40 }}
                    defaultValue={""}
                    placeholder={t("plz_chs")}
                    name={`gwp_${key + 1}`}
                    control={control}
                    options={gwpVersionList}
                    className={`w-full ` + classNames({ "p-invalid": fieldState.error })}
                    onChange={(event) => {
                      field.onChange(event.value);
                    }}
                  />
                )}
              />
            </div>
          );
        })
      )}
      {/* 換行：讓清冊類型與納管行業別、納管條件同一列 */}
      <div className="grid col-12">
        {/* 清冊類型 - 僅新增時可異動，編輯/詳情時不可編輯 */}
        <div className="col-12 lg:col-4">
          <label htmlFor="planType" className={`form-label ${tab1Action === "detail" ? "" : "required"}`}>
            {t("planType")}
          </label>
          <Controller
            name="planType"
            control={control}
            rules={setValidForm("planType")}
            render={({ field, fieldState }) => (
              <Dropdown
                disabled={tab1Action !== "create"}
                appendTo={"self"}
                optionLabel="subitemName"
                optionValue="subitemNo"
                optionDisabled={(option) => option?.subitemNo === "0" || option?.subitemName === "通用行業"}
                value={field.value}
                defaultValue={""}
                placeholder={t("plz_chs")}
                name="planType"
                control={control}
                options={planTypeList}
                className={`w-full ` + classNames({ "p-invalid": fieldState.error })}
                onChange={(event) => {
                  field.onChange(event.value);
                }}
              />
            )}
          />
        </div>

        {/* 納管行業別、納管條件：僅在非通用行業且非製造業時顯示 */}
        {showRegulatedFields && (
          <>
            <div className="col-12 lg:col-4">
              <label htmlFor="regulatedIndustry" className="form-label">
                {t("regulatedInventory")}
              </label>
              <Controller
                name="regulatedIndustry"
                control={control}
                render={({ field, fieldState }) => (
                  <Dropdown
                    disabled={tab1Action === "detail"}
                    appendTo={"self"}
                    optionLabel="subitemName"
                    optionValue="subitemNo"
                    value={field.value}
                    defaultValue={""}
                    placeholder={t("plz_chs")}
                    name="regulatedIndustry"
                    control={control}
                    options={filteredRegulatedIndustryList}
                    className={`w-full ` + classNames({ "p-invalid": fieldState.error })}
                    onChange={(event) => {
                      field.onChange(event.value);
                      setValue("regulatedCondition", null);
                    }}
                  />
                )}
              />
            </div>
            <div className="col-12 lg:col-4">
              <label htmlFor="regulatedCondition" className="form-label">
                {t("regulatedCondition")}
              </label>
              <Controller
                name="regulatedCondition"
                control={control}
                render={({ field, fieldState }) => (
                  <Dropdown
                    disabled={tab1Action === "detail"}
                    appendTo={"self"}
                    optionLabel="subitemName"
                    optionValue="subitemNo"
                    value={field.value}
                    defaultValue={""}
                    placeholder={selectedRegulatedIndustry ? t("plz_chs") : t("regulatedCondition_plz_select_industry")}
                    name="regulatedCondition"
                    control={control}
                    options={filteredRegulatedConditionList}
                    className={`w-full ` + classNames({ "p-invalid": fieldState.error })}
                    onChange={(event) => field.onChange(event.value)}
                  />
                )}
              />
            </div>
          </>
        )}
      </div>

      <div className="col-12">
        <Controller
          name="baseYear"
          control={control}
          render={({ field, fieldState }) => (
            <>
              <Checkbox
                disabled={tab1Action === "detail"}
                inputId={field.name}
                checked={field.value}
                inputRef={field.ref}
                className={classNames({ "p-invalid": fieldState.error })}
                onChange={(e) => field.onChange(e.checked)}
              />
              <label htmlFor={field.name} className="ml-2">
                {t("isBaseYear")}
              </label>
              {getFormErrorMessage(errors, field.name)}
            </>
          )}
        />
      </div>
      <div className="col-4">
        <Controller
          name="verified"
          control={control}
          render={({ field, fieldState }) => (
            <>
              <Checkbox
                disabled={tab1Action === "detail"}
                inputId={field.name}
                checked={field.value}
                inputRef={field.ref}
                className={classNames({ "p-invalid": fieldState.error })}
                onChange={(e) => {
                  field.onChange(e.checked);
                  if (e.checked) {
                    clearErrors(["verificationAgency"]);
                  }
                }}
              />
              <label htmlFor={field.name} className="ml-2">
                {t("verifiedByThirdParty")}
              </label>
            </>
          )}
        />
      </div>
      {/* 查驗證機構 */}
      <div className="col-6">
        <Controller
          name="importanceIdentification"
          control={control}
          render={({ field, fieldState }) => (
            <>
              <Checkbox
                disabled={tab1Action === "detail"}
                inputId={field.name}
                checked={field.value}
                inputRef={field.ref}
                className={classNames({ "p-invalid": fieldState.error })}
                onChange={(e) => {
                  field.onChange(e.checked);
                  if (e.checked) {
                    clearErrors(["importanceIdentification"]);
                  }
                }}
              />
              <label htmlFor={field.name} className="ml-2">
                {t("isImportanceIdentification")}
              </label>
            </>
          )}
        />
      </div>
      {watch("verified") && (
        <>
          <div className="col-4">
            <label htmlFor="inspectionAgency" className={`form-label ${tab1Action === "detail" ? "" : "required"}`}>
              {t("verificationAgency")}
            </label>
            <Controller
              name="inspectionAgency"
              control={control}
              rules={{ required: { value: watch("verified") ? true : false, message: t("validation.required") } }}
              render={({ field, fieldState }) => (
                <Dropdown
                  disabled={tab1Action === "detail"}
                  appendTo={"self"}
                  optionLabel="subitemName"
                  optionValue="subitemNo"
                  value={field.value}
                  defaultValue={""}
                  placeholder={t("plz_chs")}
                  name="inspectionAgency"
                  control={control}
                  options={verAgencyList}
                  className={`w-full ` + classNames({ "p-invalid": fieldState.error })}
                  onChange={(event) => {
                    field.onChange(event.value);
                    // 找出選中的物件
                    const selectedItem = verAgencyList.find((item) => item.subitemNo === event.value);

                    // 存 label
                    setValue("verificationAgency", selectedItem?.subitemName || "");
                    if (event.value === "0") {
                      //環境部預設AR5 ISO預設AR6
                      setValue("gwpVersion", "5");
                    } else if (event.value === "1") {
                      setValue("gwpVersion", "6");
                    }
                  }}
                />
              )}
            />
          </div>
          <div className={"col-8"}>
            <label htmlFor="verificationAgency" className="form-label">
              &nbsp;
            </label>
            <FormInput
              withLabel={false}
              register={register}
              errors={errors}
              id="verificationAgency"
              type="text"
              disabled={tab1Action === "detail"}
              rules={{ required: { value: watch("verified") ? true : false, message: t("validation.required") } }}
              enterClick={inputCheckKeyDown}
            />
          </div>
        </>
      )}

      {/* 顯著性門檻 */}
      <div className="col-12 lg:col-4">
        <FormNumberInput
          labelClass={`form-label ${tab1Action === "detail" ? "" : "required"}`}
          withLabel={true}
          register={register}
          errors={errors}
          id="significance"
          labelText={`${t("significance")} %`}
          disabled={tab1Action === "detail"}
          rules={setValidForm("significance")}
          enterClick={inputCheckKeyDown}
          value={watch("significance")}
          onChange={(newValue) => setValue("significance", newValue)}
        />
      </div>
      {/* 實質性門檻 */}
      <div className="col-12 lg:col-4">
        <FormNumberInput
          labelClass={`form-label ${tab1Action === "detail" ? "" : "required"}`}
          withLabel={true}
          register={register}
          errors={errors}
          id="materiality"
          labelText={`${t("materiality")} %`}
          disabled={tab1Action === "detail"}
          rules={setValidForm("materiality")}
          enterClick={inputCheckKeyDown}
          value={watch("materiality")}
          onChange={(newValue) => setValue("materiality", newValue)}
        />
      </div>
      {/* 排除門檻 */}
      <div className="col-12 lg:col-4">
        <FormNumberInput
          labelClass={`form-label ${tab1Action === "detail" ? "" : "required"}`}
          withLabel={true}
          register={register}
          errors={errors}
          id="exclude"
          labelText={`${t("exclude")} %`}
          disabled={tab1Action === "detail"}
          rules={setValidForm("exclude")}
          enterClick={inputCheckKeyDown}
          value={watch("exclude")}
          onChange={(newValue) => setValue("exclude", newValue)}
        />
      </div>

      {/* 補充說明 */}
      <div className="col-12">
        <FormTextarea
          register={register}
          errors={errors}
          id="memo"
          name="memo"
          type="text"
          labelText={t("explanation")}
          areaClass="w-full"
          rules={setValidForm("memo")}
          placeholder={t("validation.limitMaxChar", { max: 200 })}
          disabled={tab1Action === "detail"}
        />
      </div>
      {tab1Action === "create" ? (
        <div className="col-12 gap-2 flex-all-center">
          <Button
            disabled={!(isValid && isDirty) || !isEditable}
            type="submit"
            label={t("button.create")}
            size="small"
          />
          {/* <Button type="submit" label={t("button.confirm")} size="small" /> */}
        </div>
      ) : (
        <>
          {tab1Action === "detail" ? (
            <div className="col-12 gap-2 flex-all-center">
              <Button
                disabled={!isEditable}
                label={t("button.edit")}
                onClick={(e) => {
                  e.preventDefault();
                  setTab1Action("update");
                }}
                type="button"
                size="small"
              />
            </div>
          ) : (
            <div className="col-12 gap-2 flex-all-center">
              <Button disabled={!isValid || !isEditable} type="submit" label={t("button.update")} size="small" />
              <Button
                outlined
                size="small"
                type="button"
                label={t("button.cancel")}
                onClick={(e) => {
                  e.preventDefault();
                  setTab1Action("detail");
                }}
              />
            </div>
          )}
        </>
      )}
    </form>
  );
};

export default Information;
